import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Shared between AgencyAdmin's "Sophia Controls" tab and the Sophia Operations
// dashboard (/sophia) -- same live schedule data, same pause/resume/cancel
// actions, in two places an operator might reasonably look for it.

export const SCHEDULE_STATUS_LABEL = {
  draft: "Draft (not active)", scheduled: "Scheduled", paused: "Paused",
  completed: "Completed", canceled: "Canceled", failed: "Failed",
};

export function SophiaSchedulesPanel({ title = true, refreshKey = 0 }) {
  const [schedules, setSchedules] = useState([]);
  const [scheduleBusyId, setScheduleBusyId] = useState("");

  const loadSchedules = () => api.get("/voice-ai/auto/schedules").then(({ data }) => setSchedules(data)).catch(() => {});
  useEffect(() => { loadSchedules(); }, [refreshKey]);

  const scheduleAction = async (id, action) => {
    setScheduleBusyId(id);
    try {
      await api.post(`/voice-ai/auto/schedules/${id}/${action}`);
      toast.success(`Schedule ${action}d`);
      loadSchedules();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || `Could not ${action} this schedule`);
    } finally { setScheduleBusyId(""); }
  };

  return (
    <div data-testid="sophia-schedules-panel">
      {title && (
        <>
          <div className="font-medium">Scheduled Sophia Calls</div>
          <p className="text-xs text-muted-foreground">
            Batches scheduled from "Call Selected with Sophia" → Schedule for Later. Each dials only the
            exact follow-ups selected when it was created, revalidated fresh at the scheduled time.
          </p>
        </>
      )}
      <table className="mt-2 w-full text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-2">When</th><th>Recipients</th><th>Identity</th><th>Status</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {schedules.map((s) => (
            <tr key={s.id} className="border-b text-xs" data-testid={`sophia-schedule-${s.id}`}>
              <td className="py-2">{s.date} {s.time} <span className="text-muted-foreground">({s.timezone})</span></td>
              <td>{(s.followup_ids || []).length}</td>
              <td>{s.communication_identity_display_name || "—"}</td>
              <td>{SCHEDULE_STATUS_LABEL[s.status] || s.status}</td>
              <td className="space-x-1 py-1">
                {s.status === "scheduled" && (
                  <>
                    <Button size="sm" variant="outline" disabled={scheduleBusyId === s.id}
                      onClick={() => scheduleAction(s.id, "pause")} data-testid={`sophia-schedule-pause-${s.id}`}>
                      Pause
                    </Button>
                    <Button size="sm" variant="destructive" disabled={scheduleBusyId === s.id}
                      onClick={() => scheduleAction(s.id, "cancel")} data-testid={`sophia-schedule-cancel-${s.id}`}>
                      Cancel
                    </Button>
                  </>
                )}
                {s.status === "paused" && (
                  <>
                    <Button size="sm" variant="outline" disabled={scheduleBusyId === s.id}
                      onClick={() => scheduleAction(s.id, "resume")} data-testid={`sophia-schedule-resume-${s.id}`}>
                      Resume
                    </Button>
                    <Button size="sm" variant="destructive" disabled={scheduleBusyId === s.id}
                      onClick={() => scheduleAction(s.id, "cancel")} data-testid={`sophia-schedule-cancel-${s.id}`}>
                      Cancel
                    </Button>
                  </>
                )}
                {s.status === "draft" && (
                  <Button size="sm" variant="destructive" disabled={scheduleBusyId === s.id}
                    onClick={() => scheduleAction(s.id, "cancel")} data-testid={`sophia-schedule-cancel-${s.id}`}>
                    Discard Draft
                  </Button>
                )}
              </td>
            </tr>
          ))}
          {schedules.length === 0 && (
            <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No scheduled Sophia calls yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
